import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import companyCalendarEventsApiService, {
  CompanyCalendarEventDTO,
} from "../../services/company-calendar-events-api";

interface CompanyCalendarEventsCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface CompanyCalendarEventsCardRef {
  openAddDialog: () => void;
}

const CompanyCalendarEventsCard = forwardRef<
  CompanyCalendarEventsCardRef,
  CompanyCalendarEventsCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<CompanyCalendarEventDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<string>(
    new Date().getFullYear().toString(),
  );
  const [formData, setFormData] = useState<CompanyCalendarEventDTO>({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    color: "#2196F3",
    isActive: true,
  });

  useEffect(() => {
    if (shouldLoad) {
      loadEvents();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  // Filter events by year
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const eventYear = event.startDate.split("-")[0];
      return eventYear === filterYear;
    });
  }, [events, filterYear]);

  // Get unique years from events
  const availableYears = useMemo(() => {
    const years = new Set(events.map((e) => e.startDate.split("-")[0]));
    // Always include current year
    years.add(new Date().getFullYear().toString());
    return Array.from(years).sort().reverse();
  }, [events]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyCalendarEventsApiService.getAllEvents();
      setEvents(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("config.errorCalendarEventLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event?: CompanyCalendarEventDTO) => {
    if (event) {
      setEditingId(event.id || null);
      setFormData(event);
    } else {
      setEditingId(null);
      setFormData({
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        color: "#2196F3",
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      color: "#2196F3",
      isActive: true,
    });
  };

  const handleSaveEvent = async () => {
    if (!formData.title || !formData.startDate || !formData.endDate) {
      setError(t("config.titleCalendarEventRequired"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await companyCalendarEventsApiService.updateEvent(editingId, formData);
        setSuccess(t("config.calendarEventUpdated"));
      } else {
        await companyCalendarEventsApiService.createEvent(formData);
        setSuccess(t("config.calendarEventCreated"));
      }

      await loadEvents();
      handleCloseDialog();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("config.errorCalendarEvent"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setEventToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (eventToDelete === null) return;

    try {
      setLoading(true);
      setError(null);
      await companyCalendarEventsApiService.deleteEvent(eventToDelete);
      setSuccess(t("config.calendarEventDeleted"));
      await loadEvents();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("config.errorCalendarEventDelete"),
      );
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setEventToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setEventToDelete(null);
  };

  const colorOptions = [
    { value: "#E74C3C", label: "Red" },
    { value: "#27AE60", label: "Green" },
    { value: "#F1C40F", label: "Yellow" },
    { value: "#5DADE2", label: "Blue" },
    { value: "#9B59B6", label: "Purple" },
    { value: "#F1948A", label: "Pink" },
    { value: "#1ABC9C", label: "Teal" },
    { value: "#F5CBA7", label: "Peach" },
  ];

  return (
    <Card sx={{ boxShadow: hideHeader ? "none" : undefined }}>
      <CardContent sx={{ p: hideHeader ? 0 : undefined }}>
        {!hideHeader && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <EventIcon /> {t("config.companyEvents")}
            </Typography>
            <Tooltip title={t("config.addNewCalendarEvent")}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                variant="contained"
                size="small"
              >
                {t("common.add")}
              </Button>
            </Tooltip>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Year Filter */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 1, mb: 3 }}
        >
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>{t("common.year")}</InputLabel>
            <Select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              label={t("common.year")}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {loading && !openDialog ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.title")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.description")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.startDate")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.endDate")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.color")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("config.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t("config.noCalendarEvents")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEvents.map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell>{event.title}</TableCell>
                      <TableCell>
                        {event.description
                          ? event.description.substring(0, 50) +
                            (event.description.length > 50 ? "..." : "")
                          : "-"}
                      </TableCell>
                      <TableCell>{event.startDate}</TableCell>
                      <TableCell>{event.endDate}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: event.color || "#2196F3",
                            border: 1,
                            borderColor: "divider",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            event.isActive
                              ? t("config.labelActive")
                              : t("config.labelInactive")
                          }
                          color={event.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Tooltip title={t("config.tooltipEdit")}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(event)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("config.tooltipDelete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(event.id!)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? t("config.editCalendarEvent")
            : t("config.addCalendarEvent")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label={t("config.title")}
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              fullWidth
              required
              size="small"
            />

            <TextField
              label={t("config.description")}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              size="small"
            />

            <TextField
              label={t("config.startDate")}
              type="date"
              value={formData.startDate}
              onChange={(e) =>
                setFormData({ ...formData, startDate: e.target.value })
              }
              fullWidth
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label={t("config.endDate")}
              type="date"
              value={formData.endDate}
              onChange={(e) =>
                setFormData({ ...formData, endDate: e.target.value })
              }
              fullWidth
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("config.color")}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ mb: 1.5 }}
              >
                {colorOptions.map((option) => (
                  <Tooltip key={option.value} title={option.label}>
                    <Box
                      onClick={() =>
                        setFormData({ ...formData, color: option.value })
                      }
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: option.value,
                        border: 2,
                        borderColor:
                          formData.color === option.value
                            ? "primary.main"
                            : "divider",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
              <TextField
                label={t("config.customColor")}
                value={formData.color || "#2196F3"}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, color: value });
                }}
                fullWidth
                size="small"
                placeholder="#XXXXXX"
                helperText="Enter hex color code (e.g., #E74C3C, #27AE60)"
                inputProps={{
                  maxLength: 7,
                  style: { fontFamily: "monospace" },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveEvent}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>{t("config.deleteCalendarEventConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

CompanyCalendarEventsCard.displayName = "CompanyCalendarEventsCard";

export default React.memo(CompanyCalendarEventsCard);
